import React, {
    useState,
    useMemo,
    useCallback,
    FC,
    FormEvent,
    ChangeEvent,
    ReactNode,
    useEffect,
    useRef,
} from "react";
import "../../styles/common.css";
import "./styling/AddContentModal.css";
import { WebsiteThemeLibrary, WebsiteStructureTheme } from "../Api/services/website/websiteThemes";
import {
    DEFAULT_SMART_LIST_SORT_OPTION,
    SMART_LIST_ADAPTER_OPTIONS,
    SMART_LIST_SORT_OPTIONS,
} from "../Api/constants";
import { uploadImageFromUri } from "../Api/services/media/imageUpload";
import { buildImageAssetUrl, fetchAssetMetadata } from "../Api/services/media/assetMetadata";
import { AddContentModalProps, AddContentOptions, LayoutMenuPosition } from "../types";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { SmartListLabelsInput } from "./SmartListLabelsInput";
import {
    SMART_LIST_GENRE_CHOICES,
    getTemplateById,
    getTemplatesForAdapter,
} from "../constants/smartListOptions";

type ContentTypeOption = {
    value:
        | "LIST"
        | "SMART_LIST"
        | "CAROUSEL"
        | "ASSET"
        | "MENU"
        | "MENU_ITEM"
        | "PAGE"
        | "WEBSITE_STRUCTURE";
    label: string;
    description: string;
    icon: ReactNode;
};

const contentTypeOptions: ContentTypeOption[] = [
    {
        value: "LIST",
        label: "List",
        description: "Collection that structures multiple assets or nested lists.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
            </svg>
        ),
    },
    {
        value: "SMART_LIST",
        label: "Smart List",
        description: "Dynamic list that automatically populates using genre or label filters.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
            </svg>
        ),
    },
    {
        value: "CAROUSEL",
        label: "Carousel",
        description: "Horizontal scrollable collection for displaying content.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M2 6h4v11H2V6zm5 13h10V4H7v15zM9 6h6v11H9V6zm9 0h4v11h-4V6z" />
            </svg>
        ),
    },
    {
        value: "ASSET",
        label: "Asset",
        description: "Media asset with image.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </svg>
        ),
    },
    {
        value: "MENU",
        label: "Menu",
        description: "Entry point that groups menu items for navigation.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" />
            </svg>
        ),
    },
    {
        value: "MENU_ITEM",
        label: "Menu Item",
        description: "Single link or route that lives inside a menu.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M3 17v2h12v-2H3zm0-6v2h18v-2H3zm0-6v2h18V5H3z" />
            </svg>
        ),
    },
    {
        value: "PAGE",
        label: "Page",
        description: "Renderable page.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M6 2c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2H6zm7 1.5L18.5 9H13V3.5zM6 20V4h6v6h6v10H6z" />
            </svg>
        ),
    },
    {
        value: "WEBSITE_STRUCTURE",
        label: "Create New App",
        description: "Configure your new application with custom branding and settings",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M3 4h18v2H3V4zm0 4h18v2H3V8zm0 4h10v2H3v-2zm0 4h10v2H3v-2zM17 12h4v8h-4v-8z" />
            </svg>
        ),
    },
];

const HEX_COLOR_MAX_LENGTH = 7;
const HEX_COMPONENT_LENGTH = 2;
const BRAND_COLOR_STEP_TINY = 10;
const BRAND_COLOR_STEP_SMALL = 30;
const BRAND_COLOR_STEP_MEDIUM = 50;
const BRAND_COLOR_STEP_LARGE = 70;
const BRAND_COLOR_ADJUSTMENTS = Object.freeze([
    -BRAND_COLOR_STEP_LARGE,
    -BRAND_COLOR_STEP_MEDIUM,
    -BRAND_COLOR_STEP_SMALL,
    -BRAND_COLOR_STEP_TINY,
    BRAND_COLOR_STEP_TINY,
    BRAND_COLOR_STEP_SMALL,
    BRAND_COLOR_STEP_MEDIUM,
    BRAND_COLOR_STEP_LARGE,
]);
const JOB_STATUS_POLL_INTERVAL_MS = 300;
const SUCCESS_FEEDBACK_DELAY_MS = 300;

const defaultOption = contentTypeOptions[0] ?? {
    value: "LIST",
    label: "List",
    description: "Collection that structures multiple assets or nested lists.",
    icon: null,
};

const defaultThemeId = WebsiteThemeLibrary.defaultTheme();

const buildFriendlyJobError = (rawMessage: string | null): string => {
    if (!rawMessage) {
        return "Website creation failed. Please try again in a moment.";
    }

    if (/Error downloading image/i.test(rawMessage)) {
        const urlMatch = rawMessage.match(/Error downloading image from ([^:]+):?/i);
        const source = urlMatch?.[1] ?? "the configured image URL";
        return `We couldn't download the theme image from ${source}. Please verify the URL is reachable or choose a different logo.`;
    }

    if (/API_ERROR:\s*422/i.test(rawMessage)) {
        return "The Vimond API rejected part of the request. Double-check the required fields (logo, theme colors, etc.) and try again.";
    }

    if (/Worker responded with 500/i.test(rawMessage)) {
        return "Something went wrong while creating the site. Please try again, or adjust your inputs before resubmitting.";
    }

    return rawMessage;
};

const AddContentModal: FC<AddContentModalProps> = ({
    onClose,
    onAddContent,
    token,
    onWebsiteStructureCreated,
}): React.ReactElement => {
    const [step, setStep] = useState<"selection" | "form" | "creating">("selection");
    const [contentType, setContentType] = useState<
        | "LIST"
        | "SMART_LIST"
        | "CAROUSEL"
        | "ASSET"
        | "MENU"
        | "MENU_ITEM"
        | "PAGE"
        | "WEBSITE_STRUCTURE"
    >(defaultOption.value);
    const [title, setTitle] = useState("");
    const [alias, setAlias] = useState("");
    const [pageSlug, setPageSlug] = useState("");
    const [layoutMenuPosition, setLayoutMenuPosition] = useState<LayoutMenuPosition>("Header");
    const [position, setPosition] = useState("");
    const [description, setDescription] = useState("");
    const [brandColor, setBrandColor] = useState("#F6377A");
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<ContentTypeOption | null>(null);
    const [selectedTheme, setSelectedTheme] = useState<WebsiteStructureTheme>(defaultThemeId);
    const [logoUrl, setLogoUrl] = useState("");
    const [imageUri, setImageUri] = useState("");
    const [uploadedImagePackId, setUploadedImagePackId] = useState<string | null>(null);
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
    const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success" | "error">(
        "idle",
    );
    const [uploadError, setUploadError] = useState<string | null>(null);
    const previewRequestId = useRef(0);
    const assetMetadataRequestId = useRef(0);
    const [jobError, setJobError] = useState<string | null>(null);
    const [jobErrorDetails, setJobErrorDetails] = useState<string | null>(null);
    const [invalidField, setInvalidField] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [smartListTemplate, setSmartListTemplate] = useState("");
    const [smartListSortOption, setSmartListSortOption] = useState(DEFAULT_SMART_LIST_SORT_OPTION);
    const [smartListGenre, setSmartListGenre] = useState("");
    const [smartListLabels, setSmartListLabels] = useState<string[]>([]);
    const [smartListAdapterId, setSmartListAdapterId] = useState<number | null>(null);
    const [assetListId, setAssetListId] = useState("");
    const [assetImagePackId, setAssetImagePackId] = useState("");
    const [isFetchingAssetMetadata, setIsFetchingAssetMetadata] = useState(false);
    const [assetMetadataError, setAssetMetadataError] = useState<string | null>(null);
    const [assetPreviewUrl, setAssetPreviewUrl] = useState<string | null>(null);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    useBodyScrollLock();
    const themeDefinitions = useMemo(() => WebsiteThemeLibrary.list(), []);
    const selectedThemeDefinition = useMemo(
        () => WebsiteThemeLibrary.get(selectedTheme),
        [selectedTheme],
    );

    const logoUrlTrimmed = logoUrl.trim();

    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    const activeOption =
        selectedOption ??
        contentTypeOptions.find((option) => option.value === contentType) ??
        defaultOption;

    const titleTrimmed = title.trim();
    const aliasTrimmed = alias.trim();
    const pageSlugTrimmed = pageSlug.trim();
    const positionTrimmed = position.trim();
    const descriptionTrimmed = description.trim();
    const brandColorTrimmed = brandColor.trim();
    const isAssetContent = contentType === "ASSET";
    const isPageSlugRequired = contentType === "PAGE";
    const isMenuItemPositionRequired = contentType === "MENU_ITEM";
    const isAliasRequired = contentType === "WEBSITE_STRUCTURE";
    const isBrandColorRequired = contentType === "WEBSITE_STRUCTURE";
    const isTitleValid = !isAssetContent || titleTrimmed.length > 0;
    const isPageSlugValid = !isPageSlugRequired || pageSlugTrimmed.length > 0;
    const isPositionValid = !isMenuItemPositionRequired || positionTrimmed.length > 0;
    const isAliasValid = !isAliasRequired || aliasTrimmed.length > 0;
    const isBrandColorValid = !isBrandColorRequired || /^#[0-9A-F]{6}$/i.test(brandColorTrimmed);
    const themeBrandColorNormalized = isBrandColorValid
        ? brandColorTrimmed.toUpperCase()
        : undefined;
    const smartListTemplateTrimmed = smartListTemplate.trim();
    const smartListSortOptionTrimmed = smartListSortOption.trim();
    const smartListGenreTrimmed = smartListGenre.trim();
    const availableSmartListTemplates = useMemo(
        () => getTemplatesForAdapter(smartListAdapterId ?? null),
        [smartListAdapterId],
    );
    const applySmartListTemplateSelection = useCallback((rawTemplateId: string) => {
        const normalizedTemplateId = rawTemplateId.trim();

        setSmartListTemplate(normalizedTemplateId);

        if (!normalizedTemplateId) {
            setSmartListSortOption(DEFAULT_SMART_LIST_SORT_OPTION);
            setSmartListGenre("");
            setSmartListLabels([]);
            return;
        }

        const matchedTemplate = getTemplateById(normalizedTemplateId);

        const templateSortOption =
            matchedTemplate?.defaultSortOption ?? DEFAULT_SMART_LIST_SORT_OPTION;
        const sortExistsInOptions = SMART_LIST_SORT_OPTIONS.some(
            ({ value }) => value === templateSortOption,
        );

        setSmartListSortOption(
            sortExistsInOptions ? templateSortOption : DEFAULT_SMART_LIST_SORT_OPTION,
        );

        if (!matchedTemplate?.requiresGenre) {
            setSmartListGenre("");
        }

        if (!matchedTemplate?.requiresLabels) {
            setSmartListLabels([]);
        }
    }, []);
    useEffect(() => {
        if (!smartListTemplateTrimmed) {
            return;
        }

        if (typeof smartListAdapterId !== "number") {
            applySmartListTemplateSelection("");
            return;
        }

        const templateMatchesAdapter =
            getTemplateById(smartListTemplateTrimmed)?.adapterId === smartListAdapterId;

        if (!templateMatchesAdapter) {
            applySmartListTemplateSelection("");
        }
    }, [smartListAdapterId, smartListTemplateTrimmed, applySmartListTemplateSelection]);
    const selectedSmartListChoice = useMemo(
        () => getTemplateById(smartListTemplateTrimmed),
        [smartListTemplateTrimmed],
    );
    const smartListGenreOptions = selectedSmartListChoice?.genreOptions ?? SMART_LIST_GENRE_CHOICES;
    const smartListTemplateRequiresGenre = selectedSmartListChoice?.requiresGenre ?? false;
    const smartListTemplateRequiresLabels = selectedSmartListChoice?.requiresLabels ?? false;
    const normalizedSmartListLabels = useMemo(() => {
        const normalized = smartListLabels
            .map((label) => label.trim())
            .filter((label) => label.length > 0);
        const firstLabel = normalized[0];
        return firstLabel ? [firstLabel] : [];
    }, [smartListLabels]);
    const assetListIdTrimmed = assetListId.trim();
    const assetImagePackIdTrimmed = assetImagePackId.trim();
    const hasValidRecommendationEngine =
        typeof smartListAdapterId === "number" &&
        smartListAdapterId > 0 &&
        SMART_LIST_ADAPTER_OPTIONS.some(({ id }) => id === smartListAdapterId);
    const templateMatchesRecommendationEngine =
        hasValidRecommendationEngine &&
        getTemplateById(smartListTemplateTrimmed)?.adapterId === smartListAdapterId;

    const handleSmartListTemplateChange = (event: ChangeEvent<HTMLSelectElement>) => {
        applySmartListTemplateSelection(event.target.value);
    };

    const handleSmartListAdapterChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const rawValue = event.target.value;

        if (!rawValue) {
            setSmartListAdapterId(null);
            applySmartListTemplateSelection("");
            return;
        }

        const parsedAdapterId = Number(rawValue);
        if (!Number.isFinite(parsedAdapterId) || parsedAdapterId <= 0) {
            setSmartListAdapterId(null);
            applySmartListTemplateSelection("");
            return;
        }

        const normalizedAdapterId = parsedAdapterId;

        setSmartListAdapterId(normalizedAdapterId);

        const templatesForAdapter = getTemplatesForAdapter(normalizedAdapterId);

        const templateToSelect = templatesForAdapter.some(
            ({ templateId }) => templateId === smartListTemplateTrimmed,
        )
            ? smartListTemplateTrimmed
            : "";

        applySmartListTemplateSelection(templateToSelect);
    };

    useEffect(() => {
        if (contentType === "SMART_LIST") {
            return;
        }

        setSmartListTemplate("");
        setSmartListSortOption(DEFAULT_SMART_LIST_SORT_OPTION);
        setSmartListGenre("");
        setSmartListAdapterId(null);
        setSmartListLabels([]);
    }, [contentType]);

    useEffect(() => {
        if (contentType !== "ASSET") {
            setAssetPreviewUrl(null);
            return;
        }

        if (!assetImagePackIdTrimmed) {
            return;
        }

        const previewUrl = buildImageAssetUrl(assetImagePackIdTrimmed, {
            width: 640,
        });
        setAssetPreviewUrl(previewUrl);
    }, [assetImagePackIdTrimmed, contentType]);

    useEffect(() => {
        if (contentType !== "ASSET") {
            return;
        }

        const trimmedAssetId = assetListIdTrimmed;

        if (!trimmedAssetId) {
            assetMetadataRequestId.current += 1;
            setIsFetchingAssetMetadata(false);
            setAssetMetadataError(null);
            setTitle("");
            setAssetImagePackId("");
            setAssetPreviewUrl(null);
            return;
        }

        setAssetMetadataError(null);

        const requestId = assetMetadataRequestId.current + 1;
        const debounceTimeout = setTimeout(() => {
            assetMetadataRequestId.current = requestId;
            setIsFetchingAssetMetadata(true);

            fetchAssetMetadata(trimmedAssetId)
                .then((metadata) => {
                    if (assetMetadataRequestId.current !== requestId) {
                        return;
                    }

                    if (metadata.title) {
                        setTitle(metadata.title);
                    } else {
                        setTitle("");
                    }

                    if (metadata.imagePackId) {
                        setAssetImagePackId(metadata.imagePackId);
                        const previewUrl =
                            metadata.imageUrl ??
                            buildImageAssetUrl(metadata.imagePackId, {
                                width: 640,
                            });
                        setAssetPreviewUrl(previewUrl);
                    } else {
                        setAssetImagePackId("");
                        setAssetPreviewUrl(metadata.imageUrl ?? null);
                    }

                    if (!metadata.title || !metadata.imagePackId) {
                        setAssetMetadataError(
                            "Fetched metadata is missing a title or image pack ID. Please verify the asset in Vimond.",
                        );
                    } else {
                        setAssetMetadataError(null);
                    }
                })
                .catch((error: unknown) => {
                    if (assetMetadataRequestId.current !== requestId) {
                        return;
                    }

                    const message =
                        error instanceof Error ? error.message : "Unable to fetch asset metadata.";

                    setAssetMetadataError(message);
                    setTitle("");
                    setAssetImagePackId("");
                    setAssetPreviewUrl(null);
                })
                .finally(() => {
                    if (assetMetadataRequestId.current === requestId) {
                        setIsFetchingAssetMetadata(false);
                    }
                });
        }, 400);

        return () => {
            clearTimeout(debounceTimeout);
        };
    }, [assetListIdTrimmed, contentType]);

    const isSmartListValid =
        contentType !== "SMART_LIST" ||
        (hasValidRecommendationEngine &&
            smartListTemplateTrimmed.length > 0 &&
            smartListSortOptionTrimmed.length > 0 &&
            SMART_LIST_SORT_OPTIONS.some(({ value }) => value === smartListSortOptionTrimmed) &&
            templateMatchesRecommendationEngine &&
            (!smartListTemplateRequiresGenre || smartListGenreTrimmed.length > 0) &&
            (!smartListTemplateRequiresLabels || normalizedSmartListLabels.length > 0));
    const isAssetValid =
        contentType !== "ASSET" ||
        (assetListIdTrimmed.length > 0 &&
            assetImagePackIdTrimmed.length > 0 &&
            titleTrimmed.length > 0 &&
            !assetMetadataError &&
            !isFetchingAssetMetadata);
    const isMenuItemImageValid =
        contentType !== "MENU_ITEM" || !imageUri.trim() || uploadStatus === "success";
    const isCreateDisabled =
        !isTitleValid ||
        !isPageSlugValid ||
        !isPositionValid ||
        !isAliasValid ||
        !isBrandColorValid ||
        !isSmartListValid ||
        !isAssetValid ||
        !isMenuItemImageValid;

    const ensureHashPrefixedHex = (value: string) => {
        if (!value) {
            return value;
        }
        const normalized = value.startsWith("#") ? value : `#${value}`;
        return normalized.length <= HEX_COLOR_MAX_LENGTH
            ? normalized
            : normalized.slice(0, HEX_COLOR_MAX_LENGTH);
    };

    const handleBrandColorChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = ensureHashPrefixedHex(event.target.value.replace(/[^0-9A-Fa-f#]/g, ""));
        setBrandColor(nextValue.toUpperCase());
    };

    const brandColorVariations = useMemo(() => {
        if (!/^#[0-9A-F]{6}$/i.test(brandColorTrimmed)) {
            return [];
        }

        const hexToRgb = (hex: string) => {
            const hexBody = hex.slice(1);
            const parseComponent = (start: number) =>
                parseInt(hexBody.slice(start, start + HEX_COMPONENT_LENGTH), 16);
            return {
                r: parseComponent(0),
                g: parseComponent(HEX_COMPONENT_LENGTH),
                b: parseComponent(HEX_COMPONENT_LENGTH * 2),
            };
        };

        const rgbToHex = (r: number, g: number, b: number) => {
            const toHex = (value: number) => value.toString(16).padStart(2, "0");
            return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
        };

        const adjust = (value: number, amount: number) =>
            Math.max(0, Math.min(255, value + amount));

        const base = hexToRgb(brandColorTrimmed);
        return BRAND_COLOR_ADJUSTMENTS.map((delta) =>
            rgbToHex(adjust(base.r, delta), adjust(base.g, delta), adjust(base.b, delta)),
        );
    }, [brandColorTrimmed]);

    const colorPalette = [
        "#F6377A",
        "#E91E63",
        "#9C27B0",
        "#673AB7",
        "#3F51B5",
        "#2196F3",
        "#03A9F4",
        "#00BCD4",
        "#009688",
        "#4CAF50",
        "#8BC34A",
        "#CDDC39",
        "#FFEB3B",
        "#FFC107",
        "#FF9800",
        "#FF5722",
        "#795548",
        "#9E9E9E",
        "#607D8B",
        "#000000",
    ];

    const handleColorPreviewClick = () => {
        setColorPickerOpen(!colorPickerOpen);
    };

    const handleColorSelect = (color: string) => {
        setBrandColor(color);
        setColorPickerOpen(false);
    };

    const resetFormFields = () => {
        setTitle("");
        setAlias("");
        setPageSlug("");
        setLayoutMenuPosition("Header");
        setPosition("");
        setDescription("");
        setBrandColor("#F6377A");
        setColorPickerOpen(false);
        setSelectedTheme(defaultThemeId);
        setLogoUrl("");
        setImageUri("");
        setUploadedImagePackId(null);
        setUploadedImageUrl(null);
        setUploadStatus("idle");
        setUploadError(null);
        previewRequestId.current += 1;
        setSmartListTemplate("");
        setSmartListSortOption(DEFAULT_SMART_LIST_SORT_OPTION);
        setSmartListGenre("");
        setSmartListAdapterId(null);
        setSmartListLabels([]);
        setAssetListId("");
        setAssetImagePackId("");
        setAssetMetadataError(null);
        setIsFetchingAssetMetadata(false);
        setAssetPreviewUrl(null);
        setInvalidField(null);
    };

    const handleModalClose = () => {
        setStep("selection");
        setSelectedOption(null);
        setContentType(defaultOption.value);
        resetFormFields();
        onClose();
    };

    const handleContentTypeSelect = (option: ContentTypeOption) => {
        setSelectedOption(option);
        setContentType(option.value);
        resetFormFields();
        setStep("form");
    };

    const handleBackToSelection = () => {
        setStep("selection");
        setSelectedOption(null);
        setContentType(defaultOption.value);
        resetFormFields();
    };

    const handleImageUpload = async () => {
        const trimmedUri = imageUri.trim();

        if (!trimmedUri) {
            setUploadError("Please provide a valid image URL.");
            setUploadStatus("error");
            return;
        }

        setUploadStatus("uploading");
        setUploadError(null);
        setUploadedImagePackId(null);
        setUploadedImageUrl(null);

        try {
            const result = await uploadImageFromUri(trimmedUri);

            // Verify the uploaded image exists before marking as success
            if (result.id) {
                const verificationUrl = buildImageAssetUrl(result.id, { width: 300 });

                if (!verificationUrl) {
                    setUploadStatus("error");
                    setUploadError(
                        "Invalid image pack ID received. The image may not have been uploaded correctly.",
                    );
                    return;
                }

                try {
                    const imgCheck = await fetch(verificationUrl, { method: "HEAD" });
                    if (!imgCheck.ok) {
                        setUploadStatus("error");
                        setUploadError(
                            `Image verification failed (HTTP ${imgCheck.status}). The image may not be accessible. Please try a different image URL.`,
                        );
                        return;
                    }
                } catch (fetchError) {
                    setUploadStatus("error");
                    setUploadError(
                        "Failed to verify the uploaded image. Please check your network connection and try again.",
                    );
                    return;
                }
            }

            setUploadedImagePackId(result.id);
            setUploadedImageUrl(result.imageUrl);
            setUploadStatus("success");
        } catch (error) {
            setUploadStatus("error");
            setUploadError(error instanceof Error ? error.message : "Failed to upload image");
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (isCreateDisabled) {
            return;
        }

        const submitOptions: AddContentOptions = {};

        if ((contentType === "LIST" || contentType === "WEBSITE_STRUCTURE") && aliasTrimmed) {
            submitOptions.alias = aliasTrimmed;
        }

        if (contentType === "SMART_LIST") {
            submitOptions.isSmartList = true;
            submitOptions.smartListTemplate = smartListTemplateTrimmed;
            submitOptions.smartListSortOption = smartListSortOptionTrimmed;
            if (typeof smartListAdapterId === "number" && smartListAdapterId > 0) {
                submitOptions.smartListAdapterId = smartListAdapterId;
            }
            if (smartListTemplateRequiresLabels) {
                // Label-based templates: use smartListLabels array only
                if (normalizedSmartListLabels.length > 0) {
                    submitOptions.smartListLabels = normalizedSmartListLabels;
                }
            } else {
                // Genre-based templates: use smartListGenre string only
                submitOptions.smartListGenre = smartListGenreTrimmed;
            }
        }

        if (contentType === "WEBSITE_STRUCTURE") {
            if (descriptionTrimmed) {
                submitOptions.description = descriptionTrimmed;
            }
            if (themeBrandColorNormalized) {
                submitOptions.themeBrandColor = themeBrandColorNormalized;
            }
            submitOptions.themeId = selectedTheme;
            if (logoUrlTrimmed) {
                submitOptions.logoUrl = logoUrlTrimmed;
            }
            if (token) {
                submitOptions.token = token;
            }
        }

        if (contentType === "PAGE") {
            if (pageSlugTrimmed) {
                submitOptions.pageSlug = pageSlugTrimmed;
            }
        }

        if (contentType === "ASSET") {
            submitOptions.assetListId = assetListIdTrimmed;
            submitOptions.assetImagePackId = assetImagePackIdTrimmed;
        }

        if (contentType === "MENU") {
            submitOptions.layoutMenuPosition = layoutMenuPosition;
        }

        if (contentType === "MENU_ITEM") {
            if (positionTrimmed) {
                submitOptions.position = positionTrimmed;
            }
            if (uploadedImagePackId) {
                submitOptions.imagePackId = uploadedImagePackId;
            }
        }

        // Verify menu item image exists before creating
        if (contentType === "MENU_ITEM" && uploadedImagePackId) {
            const imageUrl = buildImageAssetUrl(uploadedImagePackId, { width: 300 });
            if (imageUrl) {
                try {
                    const imgCheck = await fetch(imageUrl, { method: "HEAD" });
                    if (!imgCheck.ok) {
                        setUploadStatus("error");
                        setUploadError(
                            "The uploaded image could not be found. Please upload the image again.",
                        );
                        return;
                    }
                } catch (error) {
                    setUploadStatus("error");
                    setUploadError(
                        "Failed to verify image existence. Please check your network connection and try again.",
                    );
                    return;
                }
            }
        }

        if (contentType === "WEBSITE_STRUCTURE") {
            if (isSubmitting) {
                return;
            }
            setIsSubmitting(true);
            setStep("creating");
            setJobError(null);
            setJobErrorDetails(null);
            setInvalidField(null);

            try {
                const parentListId = window.location.pathname.split("/").pop();

                const templateResponse = await fetch("/api/template/generate", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name: selectedThemeDefinition.name,
                        description: selectedThemeDefinition.description,
                        pages: selectedThemeDefinition.pages,
                        menus: selectedThemeDefinition.menus,
                        logoTargetUrl: selectedThemeDefinition.logoTargetUrl,
                    }),
                });

                const templatePayload = await templateResponse.json().catch(() => null);

                if (!templateResponse.ok || !templatePayload) {
                    throw new Error(
                        templatePayload?.error || "Failed to generate website template",
                    );
                }

                const response = await fetch("/api/websiteJobs/start", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        parentId: parentListId,
                        title: titleTrimmed,
                        alias: aliasTrimmed,
                        description: descriptionTrimmed,
                        themeBrandColor: themeBrandColorNormalized,
                        themeId: selectedTheme,
                        logoUrl: logoUrlTrimmed || undefined,
                        bearerToken: token,
                        template: templatePayload,
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();

                    // Special handling for rate limiting
                    if (response.status === 429) {
                        throw new Error(
                            "Rate limit exceeded. You have created too many apps recently. Please wait up to 10 minutes and try again.",
                        );
                    }

                    // Capture which field caused the error
                    if (errorData.field) {
                        setInvalidField(errorData.field);
                    }

                    throw new Error(errorData.error || "Failed to start website creation");
                }

                const { jobId } = await response.json();

                const pollStatus = async () => {
                    try {
                        const statusResponse = await fetch(
                            `/api/websiteJobs/status?jobId=${jobId}`,
                        );

                        if (!statusResponse.ok) {
                            throw new Error("Failed to fetch job status");
                        }

                        const status = await statusResponse.json();

                        if (status.status === "COMPLETED") {
                            if (pollIntervalRef.current) {
                                clearInterval(pollIntervalRef.current);
                                pollIntervalRef.current = null;
                            }

                            setIsSubmitting(false);
                            setJobError(null);
                            setJobErrorDetails(null);

                            const rootListId = status.result?.rootList?.listId ?? null;

                            handleModalClose();
                            if (typeof onWebsiteStructureCreated === "function") {
                                await onWebsiteStructureCreated({
                                    jobId,
                                    rootListId,
                                    status,
                                });
                            }
                            return;
                        } else if (status.status === "FAILED") {
                            if (pollIntervalRef.current) {
                                clearInterval(pollIntervalRef.current);
                                pollIntervalRef.current = null;
                            }

                            const errorMessage = status.error?.message || "Website creation failed";

                            if (errorMessage === "TOKEN_EXPIRED") {
                                setJobError(
                                    "Your authentication token has expired. Please refresh your token and try again.",
                                );
                                setJobErrorDetails(errorMessage);
                            } else {
                                setJobError(buildFriendlyJobError(errorMessage));
                                setJobErrorDetails(errorMessage);
                            }
                        }
                    } catch (pollError: unknown) {
                        if (pollIntervalRef.current) {
                            clearInterval(pollIntervalRef.current);
                            pollIntervalRef.current = null;
                        }
                        const error = pollError as Error;
                        setJobError(buildFriendlyJobError(error.message));
                        setJobErrorDetails(error.message);
                    }
                };
                await pollStatus();
                pollIntervalRef.current = setInterval(() => {
                    void pollStatus();
                }, JOB_STATUS_POLL_INTERVAL_MS);
            } catch (error: unknown) {
                const err = error as Error;
                setJobError(buildFriendlyJobError(err.message));
                setJobErrorDetails(err.message);
                setStep("form");
                setIsSubmitting(false);
            }
        } else {
            setStep("creating");
            await new Promise((resolve) => setTimeout(resolve, 100));
            try {
                onAddContent(contentType, titleTrimmed, submitOptions);
                await new Promise((resolve) => setTimeout(resolve, SUCCESS_FEEDBACK_DELAY_MS));
                handleModalClose();
            } catch (error) {
                setStep("form");
            }
        }
    };

    return (
        <div className="modal-overlay">
            <div
                className={`modal-content add-modal-content ${step === "selection" ? "selection-mode" : "form-mode"}`}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={handleModalClose}
                    className="close-button"
                    aria-label="Close add content modal"
                >
                    &times;
                </button>
                {step === "creating" && (
                    <button
                        type="button"
                        className="back-button"
                        aria-label="Back to configuration"
                        onClick={() => {
                            setStep("form");
                            setJobError(null);
                            setJobErrorDetails(null);
                            setIsSubmitting(false);
                        }}
                    >
                        &larr;
                    </button>
                )}
                {step === "selection" && (
                    <div className="content-type-selection">
                        <h2>Choose content to add</h2>
                        <p className="selection-subtitle">
                            Pick a module type to configure its details.
                        </p>
                        <div className="content-type-grid">
                            {contentTypeOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className="content-type-card"
                                    data-type={option.value}
                                    onClick={() => handleContentTypeSelect(option)}
                                >
                                    <span className="card-icon">{option.icon}</span>
                                    <span className="card-label">{option.label}</span>
                                    <span className="card-description">{option.description}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {step === "form" && (
                    <>
                        <div className="modal-header">
                            <button
                                type="button"
                                className="back-button"
                                onClick={handleBackToSelection}
                                aria-label="Back to content type selection"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                                </svg>
                            </button>
                            <h2>Add {activeOption.label}</h2>
                        </div>
                        <p className="form-subtitle">{activeOption.description}</p>
                        <form onSubmit={handleSubmit} className="content-type-form">
                            <div className="content-type-chip">{activeOption.label}</div>
                            {contentType === "WEBSITE_STRUCTURE" && (
                                <section
                                    className="app-theme-section"
                                    aria-labelledby="app-theme-heading"
                                >
                                    <div className="app-theme-header">
                                        <h3 id="app-theme-heading">App Theme</h3>
                                    </div>
                                    <div className="app-theme-grid">
                                        {themeDefinitions.map((theme) => {
                                            const isSelectedTheme = theme.id === selectedTheme;
                                            return (
                                                <button
                                                    key={theme.id}
                                                    type="button"
                                                    className={`theme-card${isSelectedTheme ? " selected" : ""}`}
                                                    onClick={() => setSelectedTheme(theme.id)}
                                                    aria-pressed={isSelectedTheme}
                                                    aria-label={`Select ${theme.name} theme`}
                                                >
                                                    <div className="theme-card-header">
                                                        <span className="theme-card-title">
                                                            {theme.name}
                                                        </span>
                                                        <span
                                                            className={`theme-card-status${isSelectedTheme ? " selected" : ""}`}
                                                        >
                                                            {isSelectedTheme
                                                                ? "Selected"
                                                                : "Select"}
                                                        </span>
                                                    </div>
                                                    <p className="theme-card-description">
                                                        {theme.description}
                                                    </p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}
                            <div className="form-panel">
                                {!isAssetContent && (
                                    <div className="form-group">
                                        <label htmlFor="title">
                                            Title{" "}
                                            <span
                                                className="field-indicator required"
                                                aria-hidden="true"
                                            >
                                                *
                                            </span>
                                        </label>
                                        <input
                                            id="title"
                                            type="text"
                                            value={title}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                setTitle(e.target.value)
                                            }
                                            placeholder="Enter a title"
                                            required
                                            aria-required="true"
                                        />
                                    </div>
                                )}

                                {(contentType === "LIST" ||
                                    contentType === "WEBSITE_STRUCTURE") && (
                                    <div className="form-group">
                                        <label htmlFor="alias">
                                            Alias{" "}
                                            {contentType === "WEBSITE_STRUCTURE" ? (
                                                <span
                                                    className="field-indicator required"
                                                    aria-hidden="true"
                                                >
                                                    *
                                                </span>
                                            ) : (
                                                <span className="field-indicator optional">
                                                    (optional)
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            id="alias"
                                            type="text"
                                            value={alias}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                setAlias(e.target.value)
                                            }
                                            placeholder="e.g., fakeflix"
                                            required={contentType === "WEBSITE_STRUCTURE"}
                                            aria-required={contentType === "WEBSITE_STRUCTURE"}
                                        />
                                        <p className="field-helper">
                                            {contentType === "WEBSITE_STRUCTURE"}
                                        </p>
                                    </div>
                                )}

                                {contentType === "SMART_LIST" && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="smartListRecommendationEngineId">
                                                Recommendation Engine{" "}
                                                <span
                                                    className="field-indicator required"
                                                    aria-hidden="true"
                                                >
                                                    *
                                                </span>
                                            </label>
                                            <select
                                                id="smartListRecommendationEngineId"
                                                value={
                                                    typeof smartListAdapterId === "number"
                                                        ? smartListAdapterId.toString()
                                                        : ""
                                                }
                                                onChange={handleSmartListAdapterChange}
                                                required
                                            >
                                                <option value="">
                                                    Select recommendation engine...
                                                </option>
                                                {SMART_LIST_ADAPTER_OPTIONS.map(({ id, name }) => (
                                                    <option key={id} value={id}>
                                                        {name}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="field-helper" />
                                        </div>

                                        {hasValidRecommendationEngine && (
                                            <div className="form-group">
                                                <label htmlFor="template">
                                                    Template{" "}
                                                    <span
                                                        className="field-indicator required"
                                                        aria-hidden="true"
                                                    >
                                                        *
                                                    </span>
                                                </label>
                                                <select
                                                    id="template"
                                                    value={smartListTemplate}
                                                    onChange={handleSmartListTemplateChange}
                                                    required
                                                    disabled={
                                                        availableSmartListTemplates.length === 0
                                                    }
                                                >
                                                    <option value="">
                                                        {availableSmartListTemplates.length === 0
                                                            ? "No templates available"
                                                            : "Select template..."}
                                                    </option>
                                                    {availableSmartListTemplates.map(
                                                        ({ templateId, adapterId, label }) => (
                                                            <option
                                                                key={templateId}
                                                                value={templateId}
                                                                data-recommendation-engine-id={
                                                                    adapterId
                                                                }
                                                            >
                                                                {label}
                                                            </option>
                                                        ),
                                                    )}
                                                </select>
                                                {selectedSmartListChoice?.description && (
                                                    <p className="field-helper">
                                                        {selectedSmartListChoice.description}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {hasValidRecommendationEngine &&
                                            smartListTemplateTrimmed.length > 0 && (
                                                <div className="form-group">
                                                    <label htmlFor="sortOptions">
                                                        Sort Options{" "}
                                                        <span
                                                            className="field-indicator required"
                                                            aria-hidden="true"
                                                        >
                                                            *
                                                        </span>
                                                    </label>
                                                    <select
                                                        id="sortOptions"
                                                        value={smartListSortOption}
                                                        onChange={(
                                                            event: ChangeEvent<HTMLSelectElement>,
                                                        ) =>
                                                            setSmartListSortOption(
                                                                event.target.value,
                                                            )
                                                        }
                                                        required
                                                    >
                                                        {SMART_LIST_SORT_OPTIONS.map(
                                                            ({ value, label }) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ),
                                                        )}
                                                    </select>
                                                </div>
                                            )}

                                        {smartListTemplateRequiresGenre && (
                                            <div className="form-group">
                                                <label htmlFor="genre">
                                                    Genre{" "}
                                                    <span
                                                        className="field-indicator required"
                                                        aria-hidden="true"
                                                    >
                                                        *
                                                    </span>
                                                </label>
                                                <select
                                                    id="genre"
                                                    value={smartListGenre}
                                                    onChange={(
                                                        event: ChangeEvent<HTMLSelectElement>,
                                                    ) => setSmartListGenre(event.target.value)}
                                                    required
                                                >
                                                    <option value="">Select genre...</option>
                                                    {smartListGenreOptions.map(
                                                        ({ value, label }) => (
                                                            <option key={value} value={value}>
                                                                {label}
                                                            </option>
                                                        ),
                                                    )}
                                                </select>
                                                <p className="field-helper">
                                                    Select from the supported Vimond genre tags.
                                                </p>
                                            </div>
                                        )}

                                        {smartListTemplateRequiresLabels && (
                                            <div className="form-group">
                                                <label htmlFor="smartListLabelInput">
                                                    Labels{" "}
                                                    <span
                                                        className="field-indicator required"
                                                        aria-hidden="true"
                                                    >
                                                        *
                                                    </span>
                                                </label>
                                                <SmartListLabelsInput
                                                    inputId="smartListLabelInput"
                                                    labels={smartListLabels}
                                                    onLabelsChange={setSmartListLabels}
                                                    helperText="Only one label is allowed; pressing Enter saves it (entering another replaces the current tag)."
                                                    maxLabels={1}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}

                                {contentType === "ASSET" && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="assetListId">
                                                Asset ID{" "}
                                                <span
                                                    className="field-indicator required"
                                                    aria-hidden="true"
                                                >
                                                    *
                                                </span>
                                            </label>
                                            <div className="asset-id-row">
                                                <input
                                                    id="assetListId"
                                                    type="text"
                                                    value={assetListId}
                                                    onChange={(
                                                        e: ChangeEvent<HTMLInputElement>,
                                                    ) => {
                                                        setAssetListId(e.target.value);
                                                        setAssetMetadataError(null);
                                                        setTitle("");
                                                        setAssetImagePackId("");
                                                        setAssetPreviewUrl(null);
                                                    }}
                                                    placeholder="Enter a Vimond asset ID"
                                                    required={isAssetContent}
                                                    aria-required={isAssetContent}
                                                    autoComplete="off"
                                                />
                                            </div>
                                            <p className="field-helper">
                                                Title and artwork sync automatically
                                            </p>
                                            {assetMetadataError && (
                                                <p className="field-error" role="alert">
                                                    {assetMetadataError}
                                                </p>
                                            )}
                                            {assetPreviewUrl && (
                                                <div className="asset-preview">
                                                    <p className="field-helper">Artwork preview</p>
                                                    <img
                                                        src={assetPreviewUrl}
                                                        alt={`Preview for asset ${assetListIdTrimmed || ""}`}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {contentType === "WEBSITE_STRUCTURE" && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="description">Description</label>
                                            <textarea
                                                id="description"
                                                value={description}
                                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                                                    setDescription(e.target.value)
                                                }
                                                placeholder="Provide a short description"
                                                rows={3}
                                            />
                                        </div>
                                        <div className="brand-color-picker">
                                            <label htmlFor="brandColor">
                                                Brand Color{" "}
                                                <span
                                                    className="field-indicator required"
                                                    aria-hidden="true"
                                                >
                                                    *
                                                </span>
                                            </label>
                                            <div className="brand-color-input-row">
                                                <div className="brand-color-preview-wrapper">
                                                    <button
                                                        type="button"
                                                        className="brand-color-preview"
                                                        style={{
                                                            backgroundColor: isBrandColorValid
                                                                ? brandColorTrimmed
                                                                : "#CDD2DC",
                                                        }}
                                                        onClick={handleColorPreviewClick}
                                                        aria-label="Open color picker"
                                                    />
                                                    {colorPickerOpen && (
                                                        <div className="color-picker-popup">
                                                            <div className="color-picker-header">
                                                                <span>Choose a Color</span>
                                                                <button
                                                                    type="button"
                                                                    className="color-picker-close"
                                                                    onClick={() =>
                                                                        setColorPickerOpen(false)
                                                                    }
                                                                    aria-label="Close color picker"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                            <div className="color-palette-grid">
                                                                {colorPalette.map((color) => (
                                                                    <button
                                                                        key={color}
                                                                        type="button"
                                                                        className={`color-palette-swatch${brandColorTrimmed === color ? " active" : ""}`}
                                                                        style={{
                                                                            backgroundColor: color,
                                                                        }}
                                                                        onClick={() =>
                                                                            handleColorSelect(color)
                                                                        }
                                                                        aria-label={`Select color ${color}`}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    id="brandColor"
                                                    type="text"
                                                    value={brandColor}
                                                    onChange={handleBrandColorChange}
                                                    placeholder="#F6377A"
                                                    className={
                                                        isBrandColorValid
                                                            ? ""
                                                            : "brand-color-input-error"
                                                    }
                                                    required={contentType === "WEBSITE_STRUCTURE"}
                                                    aria-required={
                                                        contentType === "WEBSITE_STRUCTURE"
                                                    }
                                                    aria-invalid={!isBrandColorValid}
                                                />
                                            </div>
                                            {brandColorVariations.length > 0 && (
                                                <div className="brand-color-variations">
                                                    <span className="variations-label">
                                                        Color Variations
                                                    </span>
                                                    <div className="variations-swatches">
                                                        {brandColorVariations.map((variation) => (
                                                            <button
                                                                key={variation}
                                                                type="button"
                                                                className={`variation-swatch${variation === brandColorTrimmed ? " active" : ""}`}
                                                                style={{
                                                                    backgroundColor: variation,
                                                                }}
                                                                onClick={() =>
                                                                    setBrandColor(variation)
                                                                }
                                                                aria-label={`Use color ${variation}`}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-group logo-url-group">
                                            <label htmlFor="logoUrl">
                                                Logo URL{" "}
                                                <span className="field-indicator optional">
                                                    (optional)
                                                </span>
                                            </label>
                                            <input
                                                id="logoUrl"
                                                type="url"
                                                value={logoUrl}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    setLogoUrl(e.target.value);
                                                    if (invalidField === "logoUrl") {
                                                        setInvalidField(null);
                                                    }
                                                }}
                                                placeholder="https://example.com/logo.png"
                                                aria-describedby="logo-url-helper"
                                                className={
                                                    invalidField === "logoUrl" ? "input-error" : ""
                                                }
                                                aria-invalid={invalidField === "logoUrl"}
                                            />
                                            <p id="logo-url-helper" className="field-helper">
                                                Provide a direct image link. The logo will be
                                                uploaded automatically when you create the app.
                                            </p>
                                        </div>
                                    </>
                                )}

                                {contentType === "PAGE" && (
                                    <div className="form-group">
                                        <label htmlFor="pageSlug">Page Slug (URI)</label>
                                        <input
                                            id="pageSlug"
                                            type="text"
                                            value={pageSlug}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                setPageSlug(e.target.value)
                                            }
                                            placeholder="e.g., /Browse"
                                            required={contentType === "PAGE"}
                                            aria-required={contentType === "PAGE"}
                                        />
                                    </div>
                                )}

                                {contentType === "MENU" && (
                                    <div className="form-group">
                                        <label htmlFor="layoutMenuPosition">
                                            Layout Menu Position
                                        </label>
                                        <select
                                            id="layoutMenuPosition"
                                            value={layoutMenuPosition}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                setLayoutMenuPosition(
                                                    e.target.value as LayoutMenuPosition,
                                                )
                                            }
                                        >
                                            <option value="Header">Header</option>
                                            <option value="Footer">Footer</option>
                                        </select>
                                    </div>
                                )}

                                {contentType === "MENU_ITEM" && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="position">Position (URL/Slug)</label>
                                            <input
                                                id="position"
                                                type="text"
                                                value={position}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                    setPosition(e.target.value)
                                                }
                                                placeholder="e.g., /home or https://example.com"
                                                required={contentType === "MENU_ITEM"}
                                                aria-required={contentType === "MENU_ITEM"}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="imageUri">
                                                Menu Item Image{" "}
                                                <span className="field-indicator optional">
                                                    (optional)
                                                </span>
                                            </label>
                                            <div className="image-upload-wrapper">
                                                <input
                                                    id="imageUri"
                                                    type="url"
                                                    value={imageUri}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                        setImageUri(e.target.value)
                                                    }
                                                    placeholder="https://example.com/image.png"
                                                    aria-describedby="image-uri-helper"
                                                />
                                                <button
                                                    type="button"
                                                    className="upload-button"
                                                    onClick={handleImageUpload}
                                                    disabled={
                                                        !imageUri.trim() ||
                                                        uploadStatus === "uploading"
                                                    }
                                                >
                                                    {uploadStatus === "uploading"
                                                        ? "Uploading..."
                                                        : "Upload Image"}
                                                </button>
                                            </div>
                                            {uploadStatus === "success" && uploadedImageUrl && (
                                                <div className="upload-success">
                                                    <p className="success-message">
                                                        ✓ Image uploaded successfully!
                                                    </p>
                                                    <div className="image-preview-container">
                                                        <img
                                                            src={uploadedImageUrl}
                                                            alt="Uploaded preview"
                                                            className="uploaded-image-preview"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {uploadStatus === "error" && uploadError && (
                                                <p className="error-message">✗ {uploadError}</p>
                                            )}
                                            {imageUri.trim() &&
                                                uploadStatus !== "success" &&
                                                uploadStatus !== "uploading" && (
                                                    <p
                                                        className="field-warning"
                                                        role="alert"
                                                        style={{
                                                            color: "#FF9800",
                                                            fontSize: "14px",
                                                            marginTop: "8px",
                                                        }}
                                                    >
                                                        ⚠ You must upload the image or clear the
                                                        field to create this menu item.
                                                    </p>
                                                )}
                                            <p id="image-uri-helper" className="field-helper">
                                                Provide a direct image link, then click "Upload
                                                Image" to upload it to Vimond.
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    onClick={handleModalClose}
                                    className="cancel-button"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="add-button"
                                    disabled={isCreateDisabled}
                                    aria-disabled={isCreateDisabled}
                                >
                                    Create
                                </button>
                            </div>
                        </form>
                    </>
                )}
                {step === "creating" && (
                    <div className="job-progress-container">
                        <div className="loading-spinner">
                            <svg className="spinner-icon" viewBox="0 0 50 50">
                                <circle
                                    className="spinner-path"
                                    cx="25"
                                    cy="25"
                                    r="20"
                                    fill="none"
                                    strokeWidth="4"
                                />
                            </svg>
                        </div>
                        <h2 style={{ marginTop: "24px", marginBottom: "12px" }}>
                            {contentType === "WEBSITE_STRUCTURE"
                                ? "Generating Your Website..."
                                : `Creating ${activeOption.label}...`}
                        </h2>
                        <p
                            style={{
                                color: "#8B92A6",
                                fontSize: "15px",
                                lineHeight: "1.6",
                                maxWidth: "400px",
                                margin: "0 auto",
                            }}
                        >
                            {contentType === "WEBSITE_STRUCTURE"
                                ? "Please wait while we create your pages, menus, and structure. This usually takes just a few seconds."
                                : "Please wait while we create your content. This usually takes just a moment."}
                        </p>
                        {jobError && (
                            <div className="error-message" style={{ marginTop: "20px" }}>
                                <strong>Error:</strong> {jobError}
                                {jobErrorDetails && (
                                    <details
                                        style={{
                                            fontSize: "13px",
                                            color: "#8B92A6",
                                            marginTop: "8px",
                                        }}
                                    >
                                        <summary>Read more</summary>
                                        <pre
                                            style={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                marginTop: "8px",
                                            }}
                                        >
                                            {jobErrorDetails}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddContentModal;
